import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { MsalProvider } from '@azure/msal-react';
import { ToastContainer } from 'react-toastify';
import { ErrorBoundary } from 'react-error-boundary';

import App from './App.tsx';
import { msalInstance } from './services/auth/msalInstance';
import ErrorFallback from './components/common/ErrorFallback';
import './index.css';
import 'react-toastify/dist/ReactToastify.css';

// Initialize MSAL and handle redirects
const initializeMsal = async () => {
  await msalInstance.initialize();
  
  // Handle redirect promises (important for redirect flow)
  try {
    await msalInstance.handleRedirectPromise();
  } catch (error) {
    console.error('Login redirect failed:', error);
  }
};

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Initialize MSAL before rendering, so ProtectedRoute never sees a transient
// unauthenticated state on reload (it would bounce through /login to /).
initializeMsal().then(() => {
  ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error, errorInfo) => {
        console.error('Application error:', error, errorInfo);
        // TODO: Send error to monitoring service
      }}
    >
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />
          </BrowserRouter>
          {import.meta.env?.DEV && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
      </MsalProvider>
    </ErrorBoundary>
  </React.StrictMode>,
  );
});